import React, { useState, useEffect, useRef } from 'react';
import { sendWhatsAppMessage, openWhatsApp } from '../utils/helpers';
import { animateContactForm, sectionTitleAnimation, scrollRevealAnimation } from '../utils/animations';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [errors, setErrors] = useState({});
  const formRef = useRef(null);
  const titleRef = useRef(null);
  const sectionRef = useRef(null);

  // Validation functions
  const validateForm = () => {
    const newErrors = {};
    
    // Check for URLs in name
    if (containsURL(formData.name)) {
      newErrors.name = 'Name cannot contain links or URLs';
    }
    
    // Check for spam patterns in name
    if (containsSpam(formData.name)) {
      newErrors.name = 'Invalid name format';
    }
    
    // Validate email
    if (!isValidEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    // Check for URLs and spam in message
    if (containsURL(formData.message)) {
      newErrors.message = 'Message cannot contain links or URLs. Please describe your inquiry without external links.';
    }
    
    if (containsSpam(formData.message)) {
      newErrors.message = 'Message contains suspicious content. Please write a genuine inquiry.';
    }
    
    // Check minimum message length
    if (formData.message.trim().length < 10) {
      newErrors.message = 'Please provide more details (minimum 10 characters)';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const containsURL = (text) => {
    // Check for various URL patterns
    const urlPatterns = [
      /https?:\/\//i,
      /www\./i,
      /\.(com|net|org|io|co|in|edu|gov|us|uk|ca|de|fr|it|ru|cn|jp|au|br|za|mx|es|nl|se|no|dk|fi|pl|tr|kr|id|th|vn|ph|my|sg|hk|nz|ae|sa|eg|ng|ke|gh|tz|ug|zm|zw|bw|mw|na|sz|ls|mg|mu|re|sc|km|dj|so|er|et|sd|ss|ly|tn|dz|ma|mr|ml|ne|td|cf|cg|cd|ao|ga|gq|st|cv|gm|gn|gw|lr|sl|ci|bf|bj|tg|sn|bi|rw|dj)\b/i,
      /bit\.ly|tinyurl|shorturl|goo\.gl|ow\.ly/i,
      /\[url\]|\[link\]/i,
      /click here|visit here/i
    ];
    return urlPatterns.some(pattern => pattern.test(text));
  };

  const containsSpam = (text) => {
    // Common spam patterns
    const spamPatterns = [
      /\b(viagra|cialis|casino|lottery|winner|prize|claim now|act now|limited time|buy now|click here|free money|earn \$|make money fast|work from home|weight loss|credit score)\b/i,
      /\b(100% free|risk free|satisfaction guaranteed|no obligation|order now|call now|don't wait|hurry up)\b/i,
      /([@#])\1{2,}/,  // Repeated special characters like @@@ or ###
      /[A-Z]{10,}/,     // Too many consecutive capitals
      /(.)\1{5,}/       // Same character repeated many times
    ];
    return spamPatterns.some(pattern => pattern.test(text));
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  useEffect(() => {
    console.log('ContactSection: Initializing scroll animations');
    const timer = setTimeout(() => {
      // Section title animation
      if (titleRef.current) {
        sectionTitleAnimation(titleRef);
      }
      
      // Form animation
      if (formRef.current) {
        animateContactForm(formRef);
      }
      
      // Section reveal animation
      if (sectionRef.current) {
        scrollRevealAnimation(sectionRef, {
          direction: 'up',
          distance: 40,
          duration: 1.2
        });
      }
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear error for this field when user starts typing
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: ''
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitMessage('');
    
    // Validate form before submission
    if (!validateForm()) {
      setSubmitMessage('❌ Please fix the errors in the form before submitting.');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Try WhatsApp Business API first
      const result = await sendWhatsAppMessage(formData);
      
      if (result.success) {
        setSubmitMessage('✅ Message sent successfully! We\'ll get back to you soon.');
        // Clear form
        setFormData({ name: '', email: '', phone: '', message: '' });
        setErrors({});
      } else {
        // Fallback to WhatsApp Web
        console.log('API failed, falling back to WhatsApp Web');
        openWhatsApp(formData);
        setSubmitMessage('📱 WhatsApp opened. Please send the message to complete your inquiry.');
        // Clear form after opening WhatsApp
        setFormData({ name: '', email: '', phone: '', message: '' });
        setErrors({});
      }
    } catch (error) {
      // Fallback to WhatsApp Web on error
      console.log('Error occurred, falling back to WhatsApp Web:', error);
      openWhatsApp(formData);
      setSubmitMessage('📱 WhatsApp opened. Please send the message to complete your inquiry.');
      // Clear form after opening WhatsApp
      setFormData({ name: '', email: '', phone: '', message: '' });
      setErrors({});
    }
    
    setIsSubmitting(false);
  };

  return (
    <section ref={sectionRef} id="contact" className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 ref={titleRef} className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Contact Us</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Ready to commission a custom sculpture or have questions about our handmade art? Let's bring your vision to life!
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div>
            <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
              <div className="form-field">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>
              
              <div className="form-field">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  value={formData.message}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.message ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Please describe your sculpture requirements or inquiry..."
                ></textarea>
                {errors.message && (
                  <p className="mt-1 text-sm text-red-600">{errors.message}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Note: Links and URLs are not allowed in messages for security purposes.
                </p>
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-3 px-6 rounded-lg font-medium transition-colors ${
                  isSubmitting 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-primary-600 hover:bg-primary-700'
                } text-white`}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </span>
                ) : (
                  'Send via WhatsApp'
                )}
              </button>
              
              {/* Status Message */}
              {submitMessage && (
                <div className={`mt-4 p-4 rounded-lg text-sm ${
                  submitMessage.includes('✅') 
                    ? 'bg-green-100 text-green-700 border border-green-200' 
                    : 'bg-blue-100 text-blue-700 border border-blue-200'
                }`}>
                  {submitMessage}
                </div>
              )}
            </form>
          </div>

          {/* Contact Info & Map */}
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Contact Information</h3>
              <div className="space-y-4">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-primary-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>00, Near Ibrahimabad Bridge, Maulabad, Trevedi Ganj, Barabanki-227131, Uttar Pradesh, India</span>
                </div>
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-primary-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span>8887732619</span>
                </div>
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-primary-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span>9450686765</span>
                </div>
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-primary-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span>9161410260</span>
                </div>
              </div>
            </div>

            {/* Google Maps Embed */}
            <div className="mt-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Our Location</h3>
              <div className="rounded-lg overflow-hidden shadow-lg">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3564.6053727868803!2d81.25040127645589!3d26.693100776778945!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x399bc31fd97bff03%3A0x4752e5273520e362!2sRam%20Tirath%20Art!5e0!3m2!1sen!2sin!4v1762613557135!5m2!1sen!2sin"
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Ram Tirath Art Studio Location"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
